import { useEffect, useState } from "react";
import {
  MDSCheckbox,
  MDSCheckboxGroup,
  MDSGridList,
  MDSButton,
  MDSButtonVariant,
  MDSTextVariant,
  MDSTextWeight,
  MDSText,
  MDSTextColor,
} from "@/mds";
import { observer } from "mobx-react-lite";
import {
  StyledExplanation,
  StyledFormattingContentLeftAddon,
  StyledFormattingContentRightAddon,
  StyledHeading,
  StyledSubExplanation,
  StyledSubTitle,
} from "@/pages/integrations/shared/integrations.styles";
import { useAppStore } from "@/store";
import {
  TextMessageIntegrationOrganizingMode,
  TextMessageIntegrationOrganizingInstructions,
} from "@/store/text-message-senders/types";
import { IntegrationsFormattingCollections } from "@/pages/integrations/shared/integrations-formatting-collections";
import {
  Container,
  Section,
  OptionLabel,
  ExtraContentContainer,
  ExtraContent,
  ExtraContentInformation,
  LeftStyles,
  StyledTextField,
  StyledHorizontalDivider,
} from "@/pages/integrations/shared/integrations-formatting.styles";
import { MDSGridListItemProps } from "@/mds/components/lists/grid-list-item";

export const IntegrationsFormattingForTextMessage = observer(() => {
  const { store } = useAppStore();
  const {
    organizingOption,
    organizingInstructions,
    setOrganizingOption,
    setOrganizingInstructions,
    organizingManualCollections,
    setOrganizingManualCollections,
  } = store.textMessageSenders;

  const [localOrganizingInstructions, setLocalOrganizingInstructions] =
    useState<TextMessageIntegrationOrganizingInstructions>(organizingInstructions);

  useEffect(() => {
    setLocalOrganizingInstructions(organizingInstructions);
  }, [organizingInstructions]);

  // Compute show states from local instructions
  const showOrganizingCustomize = localOrganizingInstructions !== null;

  const isOrganizingSaveDisabled = localOrganizingInstructions === organizingInstructions;

  const showSpecificCollections = organizingOption.includes("manual");

  // Generate organizing option items
  const organizingItems: MDSGridListItemProps[] = [
    {
      id: "specific-collections",
      textValue: "Specific Collections",
      leftAddon: (
        <StyledFormattingContentLeftAddon>
          <MDSCheckbox value="manual" aria-label="Specific collections" />
        </StyledFormattingContentLeftAddon>
      ),
      children: (
        <OptionLabel>
          <MDSText
            variant={MDSTextVariant.TextS}
            weight={MDSTextWeight.Smedium}
            color={MDSTextColor.Primary}
          >
            Specific Collections
          </MDSText>
          <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
            Always add notes created from text message to one or more collections
          </MDSText>
          {showSpecificCollections && (
            <>
              <StyledHorizontalDivider />
              <ExtraContentContainer>
                <ExtraContent>
                  <IntegrationsFormattingCollections
                    organizingManualCollections={organizingManualCollections}
                    setOrganizingManualCollections={setOrganizingManualCollections}
                  />
                </ExtraContent>
              </ExtraContentContainer>
            </>
          )}
        </OptionLabel>
      ),
    },
    {
      id: "smart-organizing",
      textValue: "Smart organizing",
      leftAddon: (
        <StyledFormattingContentLeftAddon>
          <MDSCheckbox value="smart" aria-label="Smart organizing" />
        </StyledFormattingContentLeftAddon>
      ),
      children: (
        <OptionLabel>
          <MDSText
            variant={MDSTextVariant.TextS}
            weight={MDSTextWeight.Smedium}
            color={MDSTextColor.Primary}
          >
            Smart
          </MDSText>
          <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
            Allow Mem to pick the collections to organize each text message you save
          </MDSText>
          {showOrganizingCustomize && (
            <>
              <StyledHorizontalDivider />
              <ExtraContentContainer>
                <ExtraContent>
                  <ExtraContentInformation>
                    <StyledSubTitle variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
                      Customize how text messages are organized
                    </StyledSubTitle>
                    <StyledSubExplanation variant={MDSTextVariant.ParagraphS}>
                      Provide more guidance to how to organize notes from different kinds of text
                      messages
                    </StyledSubExplanation>
                  </ExtraContentInformation>
                  <StyledTextField
                    isTextArea
                    textAreaProps={{ rows: 3 }}
                    aria-label="Customize how text messages are organized"
                    value={localOrganizingInstructions}
                    onChange={setLocalOrganizingInstructions}
                    placeholder="For project updates, organize into your work projects collection. For article links, add to your reading list collection. For meal ideas, categorize in your recipe collection. For travel plans, group in your upcoming trips collection."
                    name="organizingInstructions"
                  />
                  <LeftStyles>
                    <MDSButton
                      onClick={() => {
                        // revert to local value
                        setLocalOrganizingInstructions(organizingInstructions);
                      }}
                    >
                      Cancel
                    </MDSButton>
                    <MDSButton
                      variant={MDSButtonVariant.Contrast}
                      onClick={() => setOrganizingInstructions(localOrganizingInstructions)}
                      isDisabled={isOrganizingSaveDisabled}
                    >
                      Save Settings
                    </MDSButton>
                  </LeftStyles>
                </ExtraContent>
              </ExtraContentContainer>
            </>
          )}
        </OptionLabel>
      ),
      rightAddon: !showOrganizingCustomize && (
        <StyledFormattingContentRightAddon>
          <MDSButton onClick={() => setLocalOrganizingInstructions("")}>Customize</MDSButton>
        </StyledFormattingContentRightAddon>
      ),
    },
  ];

  return (
    <Container>
      <Section>
        <StyledHeading variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
          Organizing
        </StyledHeading>
        <StyledExplanation variant={MDSTextVariant.ParagraphS}>
          Mem can automatically organize notes created from your text messages
        </StyledExplanation>
        <MDSCheckboxGroup
          aria-label="Organizing"
          value={organizingOption}
          onChange={value => {
            const typedValue = value as TextMessageIntegrationOrganizingMode[];
            setOrganizingOption(typedValue);

            if (!typedValue.includes("smart")) {
              setLocalOrganizingInstructions(null);
            }
          }}
        >
          <MDSGridList items={organizingItems} disallowTypeAhead />
        </MDSCheckboxGroup>
      </Section>
    </Container>
  );
});
